import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { logger } from 'core/utils/logger';
import { Grid, Stack, Chip, Button, Typography, TextField, InputAdornment } from '@mui/material';
import { useSnackbar } from 'notistack';
import { getInmatesPaymentData } from 'services/payments';
import DialogSlide from 'components/DialogSlide';
import FilterOptions, { paymentStatusArray } from './FilterOptions';
import { format, startOfMonth, subMonths } from "date-fns";
import { CurrencyRupeeRounded, DateRangeRounded, FilterAltRounded, HighlightOffRounded, SearchRounded, } from '@mui/icons-material';
import { red } from '@mui/material/colors';
import PaymentCard from './PaymentCard';
import RentDueReminders from './RentDueReminders';
import { AppContext } from 'core/context';
import { isRolesPermitted } from 'core/utils/common';
import { paymentTypesObj } from 'utils/appConstants';

const PaymentsPage = ({pageRef}) => {
  logger.info('[PaymentsPage] mounting...');

  const [payments, setPayments] = useState(null);
  const {enqueueSnackbar} = useSnackbar();
  const [filters, setFilters] = useState({searchText: '', data: null});
  const [multiplePaymentIds, setMultiplePaymentIds] = useState(null);
  
  // Permissions
  const { permissions } = useContext(AppContext);
  const isCollectPaymentsAllowed = useMemo(() => isRolesPermitted(['Collect payments'], permissions), [permissions]);
  const isSendRentDueRemindersAllowed = useMemo(() => isRolesPermitted(['Send rent due reminders'], permissions), [permissions]);

  const getPaymentsList = useCallback((filters={}) => {
    logger.debug('[PaymentsPage]:{getPaymentsList} fetching data');
    const controller = new AbortController();

    getInmatesPaymentData(filters, {signal: controller.signal, pageRef}).then(({response, error}) => {
      if(error?.message === 'canceled') return;
      // Return if any error
      if(error){
        setPayments([]);
        return enqueueSnackbar(error?.message, {variant: 'error'});
      }
      setPayments(response);
    });
    return controller;
  }, [pageRef, enqueueSnackbar]);

  useEffect(() => {
    logger.debug('[PaymentsPage] mounted');
    return () => {
      logger.debug('[PaymentsPage] unmounted');
    }
  }, []);

  useEffect(() => {
    let controller;
    if(filters?.data?.from) {
      const data = {
        from: filters.data.from,
        to: filters.data.to,
        status: filters.data.status,
      }
      controller = getPaymentsList(data);
    }
  
    return () => {
      controller?.abort();
    }
  }, [filters?.data?.from, filters?.data?.to, filters?.data?.status, getPaymentsList]);
  
  const updateFilters = useCallback((newFilters) => {
    setFilters(newFilters);
  }, []);

  const onPaymentSuccess = useCallback(() => {
    const data = {
      from: filters.data.from,
      to: filters.data.to,
      status: filters.data.status,
    }
    getPaymentsList(data);
  }, [filters, getPaymentsList]);
  
  function applySearchFilters(payments) {
    if(!payments) return;
    
    const { searchText='', paymentType } = filters;
    const searchFilteredPayments = searchText?.length > 0 ? payments?.filter(({inmateName, mobile, paymentType, description, checkInDate, bedInfo, paymentMode}) => (
      inmateName?.toLowerCase()?.includes(searchText?.toLowerCase()) || 
      mobile?.toLowerCase()?.includes(searchText?.toLowerCase()) ||
      paymentType?.toLowerCase()?.includes(searchText?.toLowerCase()) ||
      description?.toLowerCase()?.includes(searchText?.toLowerCase()) ||
      checkInDate?.toLowerCase()?.includes(searchText?.toLowerCase()) ||
      bedInfo?.toLowerCase()?.includes(searchText?.toLowerCase()) ||
      paymentMode?.toLowerCase()?.includes(searchText?.toLowerCase()) 
    )) : payments;

    return paymentType  && paymentType !== 'all' ? searchFilteredPayments.filter(payment => payment.paymentType === paymentType) : searchFilteredPayments;
  }

  const togglePaymentSelection = useCallback((paymentId) => {
    if(!multiplePaymentIds)return;
    if(multiplePaymentIds?.includes(paymentId)){
      setMultiplePaymentIds(multiplePaymentIds?.filter(id => id !== paymentId));
    }else{
      setMultiplePaymentIds([...multiplePaymentIds, paymentId]);
    }
  }, [multiplePaymentIds]);

  return (
    <Grid container>
      <Grid item xs={12}>
        <Filters isSendRentDueRemindersAllowed={isSendRentDueRemindersAllowed} updateFilters={updateFilters} pageRef={pageRef} enableMultiplePayments={() => setMultiplePaymentIds(prev => prev ? null : [])} />
      </Grid>
      {payments?.length === 0 ? (
        <Grid item container justifyContent="center" alignItems="center" sx={{p: 2}} direction="column">
          <CurrencyRupeeRounded sx={{color: red[50], fontSize: 80, mb: 1}} />
          <Typography variant="subtitle1" color="grey" gutterBottom>No payments to show</Typography>
        </Grid>
      ) : null}
      <Grid container item xs={12} spacing={2} mt={1} justifyContent={{xs: 'center', md: 'flex-start'}}>
        {applySearchFilters(payments)?.map(payment => 
          <PaymentCard 
            key={payment.paymentId} 
            data={payment} 
            onPaymentSuccess={onPaymentSuccess} 
            isMultiplePaymentEnable={!!multiplePaymentIds}
            togglePaymentSelection={togglePaymentSelection}
            isPaymentSelected={multiplePaymentIds?.includes(payment.paymentId)}
            isCollectPaymentsAllowed={isCollectPaymentsAllowed} 
          />
        )}
      </Grid>
    </Grid>
  );
}
 
export default PaymentsPage;

const startDate = startOfMonth(subMonths(new Date(), 1)), endDate = new Date();
const defaultFilterValues = { from: startDate, to: endDate, status: 'unpaid', searchText: '', paymentType: null};

function Filters({updateFilters, pageRef, enableMultiplePayments, isSendRentDueRemindersAllowed}) {
  const [filters, setFilters] = useState({toggle: false, data:{...defaultFilterValues}});
  const { from: startDate, to: endDate, status, paymentType } = filters?.data || {};
  const [{name}={}] = paymentStatusArray.filter(({value}) => value === status);

  const handleModalClose = useCallback(() => {
    setFilters(prevData => ({...prevData, toggle: false}));
  }, []);
  
  const applyFilters = useCallback((data) => {
    setFilters({toggle: false, data});
  }, []);
  
  useEffect(() => {
    const { from, to, status, searchText, paymentType } = filters.data;
    const formattedFilters = {
      from : format(from, 'dd-MM-yyyy'),
      to : format(to, 'dd-MM-yyyy'),
      status: !status || status === '' ? 'all' : status,
      paymentType: paymentType === 'all' ? null : paymentType
    }
    updateFilters({data: formattedFilters, searchText, paymentType});
  }, [filters.data, updateFilters]);
  
  function openFilters(e) {
    e.stopPropagation();
    setFilters((prev) => ({...prev, toggle: true}));
  }

  function removeStatusFilter(e) {
    e.stopPropagation();
    setFilters({...filters, data: {...filters.data, status: null}})
  }

  // function removeSearchString(e) {
  //   e.stopPropagation();
  //   setFilters({...filters, data: {...filters.data, searchText: ''}})
  // }

  function removePaymentTypeFilter(e) {
    e.stopPropagation();
    setFilters({...filters, data: {...filters.data, paymentType: null}})
  }

  return (
    <>
      <Grid item xs={12} >
        <Stack alignItems={{xs: 'flex-start', sm: "center"}} direction={{xs: "column-reverse", sm: 'row'}}  gap={1} justifyContent="space-between" flexWrap="wrap">
          <Stack direction="row" gap={1}  py={1} maxWidth="100%" overflow="auto">
            {startDate && endDate ? <Button
              color='tertiary'
              variant='tonal'
              sx={{flexShrink: 0}}
              startIcon={<DateRangeRounded />}  
              onClick={openFilters}
            >
              {`${format(startDate, 'dd MMM, yyyy', new Date())} - ${format(endDate, 'dd MMM, yyyy', new Date())}`}
            </Button> : null}
            {name ? <Button
              color='tertiary'
              variant='tonal'
              sx={{flexShrink: 0}}
              onClick={openFilters}
              endIcon={<HighlightOffRounded onClick={removeStatusFilter} />}  
            >
              {name}
            </Button> : null}
            {paymentType && paymentType !== 'all' ? <Button
              color='tertiary'
              variant='tonal'
              onClick={openFilters}
              sx={{flexShrink: 0}}
              // startIcon={<SearchRounded />}  
              endIcon={<HighlightOffRounded onClick={removePaymentTypeFilter} />}  
            >
              {paymentTypesObj[paymentType]}
            </Button> : null}
            {/* {searchText ? <Button
              color='tertiary'
              variant='tonal'
              onClick={openFilters}
              sx={{flexShrink: 0}}
              startIcon={<SearchRounded />}  
              endIcon={<HighlightOffRounded onClick={removeSearchString} />}  
            >
              {searchText}
            </Button> : null} */}
          </Stack>
          <Stack direction={{xs: "row-reverse", sm: "row"}} gap={1} justifyContent="space-between" sx={{width: {xs: '100%', sm: 'auto'}}}>
            {isSendRentDueRemindersAllowed ? <RentDueReminders pageRef={pageRef} /> : null}
            <Chip
              color='tertiary'
              icon={<FilterAltRounded />}
              label="Filters"
              variant="outlined"
              sx={{maxWidth: "max-content"}}
              onClick={openFilters}
              />
          </Stack>
        </Stack>
        <DialogSlide
          open={filters.toggle}
          onModalClose={handleModalClose}
          disableDialogActions={true}
          title="Filters"
          maxWidth="xs"
        >
          <FilterOptions defaultFormFields={defaultFilterValues} filters={filters.data} closeModal={handleModalClose} applyFilters={applyFilters} />  
        </DialogSlide>
      </Grid>
      <Grid xs={12} sm={10} md={8} lg={4} xl={4} >
        <TextField
          margin="dense"
          size='small'
          label="Search"
          placeholder="Name or Mobile no or Date or Bed or Payment type"
          fullWidth 
          onChange={(e) => setFilters({...filters, data: {...filters.data, searchText: e.target.value}}) }
          InputProps={{
            endAdornment: <InputAdornment position="end"><SearchRounded /></InputAdornment>
          }}
        />
      </Grid>
    </>
  );
}